import { flash } from "@xola/ui-kit";
import React from "react";
import { useMutation } from "react-query";
import { Delegate, DelegateRole } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useVisibility } from "../../../hooks/useVisibility";
import { PromptModal } from "../../Common/PromptModal";
import { SecondaryButton } from "../../Common/SecondaryButton";

interface RevokeDelegateAccessButtonProps {
    delegate?: Delegate;
    onSuccess?: () => void;
}

export const RevokeDelegateAccessButton = ({ delegate, onSuccess }: RevokeDelegateAccessButtonProps) => {
    const invalidate = useInvalidate();
    const seller = useSeller();
    const [isRevokeAccessModalOpen, { show: showRevokeAccessModal, hide: hideRevokeAccessModal }] =
        useVisibility(false);
    const [isRevokeAccessGuideModalOpen, { show: showRevokeAccessGuideModal, hide: hideRevokeAccessGuideModal }] =
        useVisibility(false);

    const { mutateAsync: deleteUser, isLoading } = useMutation(async (delegateId: ID) => {
        return api.delete(`/users/${seller.id}/delegates/${delegateId}`);
    });

    const isGuide = delegate?.permissions.some((permission) => permission.name === DelegateRole.GUIDE);
    const isUserVerified = delegate?.user.enabled;

    const handleDelete = (e: any) => {
        e.stopPropagation();

        if (isGuide) {
            showRevokeAccessGuideModal();
        } else {
            showRevokeAccessModal();
        }
    };

    const handleDeleteConfirm = async () => {
        if (delegate?.id) {
            await deleteUser(delegate.id);
            invalidate("delegates", delegate.id);
            onSuccess?.();
            flash.success(isUserVerified ? "User access revoked" : "User invite canceled");
        }

        hideRevokeAccessModal();
    };

    return (
        <>
            <SecondaryButton
                size="small"
                data-testid="delegate-item-resend-invite-revoke-access-button"
                onClick={handleDelete}
            >
                {isUserVerified ? "Revoke Access" : "Cancel invite"}
            </SecondaryButton>

            <PromptModal
                title={isUserVerified ? "Revoke Access?" : "Cancel Invite?"}
                isLoading={isLoading}
                isOpen={isRevokeAccessModalOpen}
                onConfirm={handleDeleteConfirm}
                onClose={hideRevokeAccessModal}
            >
                {`Are you sure you want to ${isUserVerified ? "revoke access" : "cancel invite"} for this user?`}
            </PromptModal>

            <PromptModal
                isOpen={isRevokeAccessGuideModalOpen}
                dialogText={{ submit: "OK" }}
                color="primary"
                onConfirm={hideRevokeAccessGuideModal}
                onClose={hideRevokeAccessGuideModal}
            >
                Please revoke guide access from <strong>Guide Management</strong> before revoking all other access for
                this user.
            </PromptModal>
        </>
    );
};
